import { useCallback, useRef, useState } from 'react';
import { closeMiniApp, off, on, qrScanner } from '@telegram-apps/sdk-react';
import { checkIsPrinterLink } from '@helpers/checkIsPrinterLink.helper.js';
import { errorLogger } from '@utils/errorLogger.util.js';

/**
 * Универсальный обработчик QR. Принимает произвольный fetchFn из стора.
 *
 * @param {Object} params
 * @param {(payload:{qrCode:string,user:{chat_id,lang,contact_id,initiator_id}})=>Promise<any>} params.fetchFn - любая async-функция из стора
 * @param {(msg:string)=>void} [params.setError] - опционально: setError из того же стора
 * @param {string} [params.redirectTo=null] - опционально: куда редиректить после успешного сканирования
 * @param {boolean} [params.enablePromoterRedirect=true] - включить логику редиректа промоутера
 * @returns {{
 *   handleQrScanner: (text: string, user?: any) => Promise<void>,
 *   redirectToPrinter: string,
 *   clearRedirectToPrinter: () => void,
 *   redirectToPromoterPage: boolean,
 *   redirectPath: string
 * }}
 */
export function useQrScanner({
  fetchFn,
  setError,
  redirectTo: redirectAfterScan = null,
  enablePromoterRedirect = true,
}) {
  const [redirectToPrinter, setRedirectToPrinter] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [redirectToPromoterPage, setRedirectToPromoterPage] = useState(false);

  const processedRef = useRef(false);

  const handleQrScanner = useCallback(
    async (text, user) => {
      if (!qrScanner.open.isAvailable() || qrScanner.isOpened()) return;

      on('scan_qr_popup_closed', closeMiniApp);

      try {
        const promise = qrScanner.open({
          text,
          onCaptured(qrCode) {
            if (processedRef.current) return false;
            processedRef.current = true;

            qrScanner.close();

            const { isPrinterLink, printerId } = checkIsPrinterLink(qrCode);

            if (isPrinterLink) {
              setRedirectToPrinter(printerId);
              return;
            }

            if (!user || !user.chat_id) {
              const err = 'Error handling QR code. User not found';
              if (setError) setError(err);
              throw new Error(err);
            }

            const { chat_id, lang, contact_id, initiator_id } = user;

            try {
              fetchFn({
                user: { chat_id, lang, contact_id, initiator_id },
                qrCode,
              })
                .catch((error) => {
                  errorLogger.error(error, { source: 'handleQrScanner' });
                  setError('Error handling QR code');
                })
                .then(() => {
                  const promoterRedirect =
                    enablePromoterRedirect &&
                    user.isPromoter &&
                    !sessionStorage.getItem('promoterRedirectDone');

                  if (promoterRedirect) {
                    sessionStorage.setItem('promoterRedirectDone', 'true');
                    setRedirectToPromoterPage(true);
                    return;
                  }

                  if (redirectAfterScan) {
                    setRedirectPath(redirectAfterScan);
                  }
                });
            } catch (error) {
              errorLogger.error(error, { source: 'handleQrScanner' });
              const msg = 'Error handling QR code';
              if (setError) setError(msg);
            }
          },
        });

        await promise;
      } catch (error) {
        errorLogger.error(error, { source: 'handleQrScanner' });
        const msg = 'Error handling QR code';
        if (setError) setError(msg);
      } finally {
        off('scan_qr_popup_closed', closeMiniApp);
      }
    },
    [setError, fetchFn, enablePromoterRedirect, redirectAfterScan],
  );

  const clearRedirectToPrinter = useCallback(() => {
    setRedirectToPrinter('');
  }, []);

  return {
    handleQrScanner,
    redirectToPrinter,
    clearRedirectToPrinter,
    redirectToPromoterPage,
    redirectPath,
  };
}
